import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Switch,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Section from "./Section";

type Props = {
  api: string;
  token?: string;
};

type Brand = {
  id: number;
  name: string;
  is_active: boolean;
  created_at: string;
};

const BrandsSection: React.FC<Props> = ({ api, token }) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Brand | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [activeInput, setActiveInput] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Brand | null>(null);
  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ open: false, msg: "", severity: "success" });

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${api}/brands`, { headers });
      if (!res.ok) throw new Error("Failed to load brands");
      setBrands(await res.json());
    } catch {
      setSnack({ open: true, msg: "Failed to load brands", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openAdd = () => {
    setEditing(null);
    setNameInput("");
    setActiveInput(true);
    setDialogOpen(true);
  };

  const openEdit = (b: Brand) => {
    setEditing(b);
    setNameInput(b.name);
    setActiveInput(b.is_active);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = nameInput.trim();
    if (!name) return;
    setSaving(true);
    try {
      const url = editing ? `${api}/brands/${editing.id}` : `${api}/brands`;
      const method = editing ? "PATCH" : "POST";
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({ name, is_active: activeInput }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      setSnack({ open: true, msg: editing ? "Brand updated" : "Brand created", severity: "success" });
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setSnack({ open: true, msg: e.message || "Save failed", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${api}/brands/${deleteTarget.id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok && res.status !== 204) throw new Error("Delete failed");
      setSnack({ open: true, msg: "Brand deleted", severity: "success" });
      setDeleteTarget(null);
      await load();
    } catch {
      setSnack({ open: true, msg: "Delete failed", severity: "error" });
    }
  };

  return (
    <>
      <Section
        title="Brands"
        subtitle="Manage product brands used for roll entry"
        rightAction={
          <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openAdd}>
            Add Brand
          </Button>
        }
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : brands.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No brands yet. Add one to get started.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brands.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>{b.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={b.is_active ? "Active" : "Inactive"}
                        size="small"
                        color={b.is_active ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(b)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(b)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? "Edit Brand" : "Add Brand"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Brand name"
            fullWidth
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            sx={{ mt: 1 }}
          />
          <Box display="flex" alignItems="center" mt={2} gap={1}>
            <Switch
              checked={activeInput}
              onChange={(e) => setActiveInput(e.target.checked)}
            />
            <Typography variant="body2">Active</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !nameInput.trim()}
          >
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Brand</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BrandsSection;
